import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { ArrowUpRight, Check } from 'lucide-react'
import { MaskLines, Reveal } from '@/components/motion/reveal'
import { CountUp } from '@/components/motion/roll-text'
import { ToneSection } from '@/components/generals/tone'
import { Logo } from '@/components/generals/logo'
import { IndustryHero } from '@/components/landing/industry/industry-hero'
import { ResourceDownload } from '@/components/landing/industry/resource-download'
import { StingBackVisual } from '@/components/landing/shared/product-visuals'
import { Testimonials } from '@/components/landing/shared/testimonials'
import { MustHaves } from '@/components/landing/shared/must-haves'
import { clients, guarantees, testimonials } from '@/constants/content'
import { resources } from '@/constants/resources'

const prepaidClients = [clients.incomm, clients.becharge]
const prepaidVoices = testimonials.filter(item => item.company === 'InComm Payments')

// A gift card that tilts with the scroll and carries its approval stamp.
const GiftCard: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
    const rotateY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [-22, 22])
    const rotateX = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [12, -12])

    return (
        <div ref={ref} aria-hidden className='w-full [perspective:1200px]'>
            <motion.div
                style={{ rotateX, rotateY }}
                className='relative aspect-[1.586] w-full max-w-sm overflow-hidden bg-gradient-to-br from-sky via-[#1a6fb0] to-navy p-6 text-white shadow-[0_50px_90px_-40px_rgba(52,181,255,0.65)]'
            >
                <span className='absolute -right-16 -top-16 size-56 rounded-full border border-white/20' />
                <span className='absolute -right-8 -top-8 size-40 rounded-full border border-white/20' />

                <div className='relative flex items-start justify-between'>
                    <Logo className='h-5 w-auto' />
                    <span className='t-label text-white/80'>
                        Gift card
                    </span>
                </div>

                <div className='absolute inset-x-6 bottom-6 flex items-end justify-between'>
                    <span className='font-mono text-sm tracking-[0.24em] text-white/80'>
                        •••• 2013
                    </span>
                    <span className='font-heading text-5xl font-semibold tracking-tight'>
                        $100
                    </span>
                </div>

                <span className='absolute left-6 top-1/2 flex -translate-y-1/2 items-center gap-2 bg-white px-3 py-1.5 t-label text-navy'>
                    <Check className='size-3.5' />
                    Approved in real time
                </span>
            </motion.div>
        </div>
    )
}

export const PrepaidIndustry: FC = (): ReactNode => {
    return (
        <>
            <IndustryHero
                tone='prepaid'
                accent='#34b5ff'
                index='03'
                name='Prepaid & Gift Cards'
                word='Prepaid'
                lines={[
                    'Peace of mind',
                    'is its own gift.',
                    <span key='treat' className='font-serif font-normal normal-case italic tracking-normal text-tone-accent wdth-100'>
                        Treat yourself.
                    </span>
                ]}
                intro='Deliver prepaid and gift cards in an instant with real-time payment fraud prevention.'
                clients={prepaidClients}
                visual={<GiftCard />}
            />

            <ToneSection tone='paper' id='origin' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='grid gap-14 lg:grid-cols-12'>
                    <div className='flex flex-col gap-6 lg:col-span-5'>
                        <span className='t-label text-tone-muted'>
                            Where we come from
                        </span>
                        <p className='font-heading text-[clamp(7rem,18vw,17rem)] font-semibold leading-[0.78] tracking-[-0.06em] wdth-62'>
                            2013
                        </p>
                        <MaskLines
                            as='h2'
                            className='font-heading t-h2 font-semibold uppercase wdth-75'
                            lines={['Born on a gift', 'card platform.']}
                        />
                    </div>

                    <div className='flex flex-col gap-6 text-[1.0625rem] leading-relaxed text-tone-muted lg:col-span-6 lg:col-start-7 lg:pt-8'>
                        <Reveal as='p' className='font-serif text-[clamp(1.6rem,2.5vw,2.4rem)] italic leading-[1.15] text-tone'>
                            Our story begins with a gift card platform we founded in 2013. After being hit with a very high chargeback rate on week one, we searched for a premium payment fraud protection solution that fits our type of product, and couldn’t find one.
                        </Reveal>
                        <Reveal as='p' delay={0.05}>
                            The market was filled with only payment fraud protection solutions that required us to decline 15%-20% of what we knew were legitimate shoppers who had gone through the entire purchase flow and provided us their payment information!
                        </Reveal>
                        <Reveal as='p' delay={0.1}>
                            To meet our specific needs for prepaid &amp; gift cards, we created our own in-house payment fraud prevention platform. It soon became clear that we weren’t the only merchant looking for such a solution.
                        </Reveal>
                        <Reveal delay={0.15}>
                            <Link href='/about-us' className='group inline-flex items-center gap-2 font-semibold text-tone'>
                                <span className='link-line'>
                                    Read our story
                                </span>
                                <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:rotate-45' aria-hidden />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </ToneSection>

            <ToneSection tone='prepaid' id='stingback' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='grid gap-16 lg:grid-cols-12 lg:items-center'>
                    <div className='flex flex-col gap-8 lg:col-span-5'>
                        <span className='t-label text-tone-muted'>
                            StingBack®
                        </span>
                        <MaskLines
                            as='h2'
                            className='font-heading t-h1 font-semibold uppercase wdth-75'
                            lines={['Show fraudsters', <span key='sting' className='text-tone-accent'>you StingBack®.</span>]}
                        />
                        <Reveal className='flex flex-col gap-4 text-tone-muted'>
                            <p>
                                nSure.ai’s patented StingBack® protocol gives fraudsters every indication their transaction was successful, even going so far as to deliver a zero-value key or download code.
                            </p>
                            <p>
                                StingBack® packs a punch in systemically dismantling the scalability of payment fraud and serves as a proactive deterrent for those who might otherwise try to defraud your company in the future.
                            </p>
                        </Reveal>
                    </div>
                    <div className='flex justify-center lg:col-span-6 lg:col-start-7'>
                        <StingBackVisual />
                    </div>
                </div>
            </ToneSection>

            <ToneSection tone='ink' id='instant' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <MaskLines
                    as='h2'
                    className='max-w-5xl font-heading t-h1 font-semibold uppercase wdth-75'
                    lines={['Instant delivery.', <span key='risk' className='text-tone-accent'>Zero risk.</span>]}
                />

                <dl className='mt-16 grid gap-px bg-tone-line sm:grid-cols-2 lg:mt-24 lg:grid-cols-4'>
                    {guarantees.map(item => {
                        const numeric = Number.parseInt(item.value, 10)
                        const suffix = item.value.replace(String(numeric), '')

                        return (
                            <div key={item.index} className='flex flex-col gap-6 bg-tone-bg p-6 md:p-8'>
                                <dt className='t-label text-tone-muted'>
                                    {item.title}
                                </dt>
                                <dd className='flex flex-col gap-3'>
                                    <span className='font-heading text-[clamp(3.5rem,6vw,5.5rem)] font-semibold leading-[0.85] tracking-[-0.045em] wdth-62'>
                                        <CountUp to={numeric} suffix={suffix} />
                                    </span>
                                    <span className='text-sm leading-relaxed text-tone-muted'>
                                        {item.text}
                                    </span>
                                </dd>
                            </div>
                        )
                    })}
                </dl>
            </ToneSection>

            <Testimonials items={prepaidVoices} tone='paper' label='Prepaid, in their words' />

            <ResourceDownload tone='void' {...resources.prepaid} />

            <MustHaves tone='prepaid' label='Built in' />
        </>
    )
}
